import React from "react";
import { AuthenticationResultStatus, authService } from "./AuthorizeService";
import { LoginActions, QueryParameterNames } from "./ApiAuthorizationConstants";
import Loading from "../BIMDigitalTwin/Common/Components/Loading";

// The main responsibility of this component is to handle the user's login process.
// This is the starting point for the login process. Any component that needs to authenticate
// a user can simply perform a redirect to this component with a returnUrl query parameter and
// let the component perform the login and return back to the return url.
export class Login extends React.PureComponent<
    { action: string },
    { message: string | null | undefined }
> {
    private static getReturnUrl(state: string | undefined) {
        const params = new URLSearchParams(window.location.search);
        const fromQuery = params.get(QueryParameterNames.ReturnUrl);
        if (fromQuery && !fromQuery.startsWith(`${window.location.origin}/`)) {
            // This is an extra check to prevent open redirects.
            throw new Error(
                "Invalid return url. The return url needs to have the same origin as the current page."
            );
        }
        return state || fromQuery || `${window.location.origin}/`;
    }

    private static redirectToApiAuthorizationPath(apiAuthorizationPath: string): void {
        const redirectUrl = `${window.location.origin}/${apiAuthorizationPath}`;
        // It's important that we do a replace here so that when the user hits the back arrow on the
        // browser they get sent back to where it was on the app instead of to an endpoint on this
        // component.
        window.location.replace(redirectUrl);
    }

    private static navigateToReturnUrl(returnUrl: string): void {
        // It's important that we do a replace here so that we remove the callback uri with the
        // fragment containing the tokens from the browser history.
        window.location.replace(returnUrl);
    }

    constructor(props: { action: string }) {
        super(props);

        this.state = {
            message: undefined,
        };
    }

    componentDidMount(): void {
        const { action } = this.props;
        switch (action) {
            case LoginActions.Login:
                this.login(Login.getReturnUrl(undefined));
                break;
            case LoginActions.LoginCallback:
                this.processLoginCallback();
                break;
            case LoginActions.LoginFailed:
                {
                    const params = new URLSearchParams(window.location.search);
                    const error = params.get(QueryParameterNames.Message);
                    this.setState({ message: error });
                }
                break;
            default:
                throw new Error(`Invalid action '${action}'`);
        }
    }

    private async login(returnUrl: string): Promise<void> {
        const result = await authService.signIn(returnUrl);
        switch (result.status) {
            case AuthenticationResultStatus.Redirect:
                break;
            case AuthenticationResultStatus.Success:
                await Login.navigateToReturnUrl(returnUrl);
                break;
            case AuthenticationResultStatus.Fail:
                this.setState({ message: result.message });
                break;
            default:
                throw new Error(`Invalid status result ${result.status}.`);
        }
    }

    private async processLoginCallback(): Promise<void> {
        const url = window.location.href;
        const result = await authService.completeSignIn(url);
        switch (result.status) {
            case AuthenticationResultStatus.Redirect:
                // There should not be any redirects as the only time completeSignIn finishes
                // is when we are doing a redirect sign in flow.
                throw new Error("Should not redirect.");
            case AuthenticationResultStatus.Success:
                await Login.navigateToReturnUrl(Login.getReturnUrl(result.returnUrl));
                break;
            case AuthenticationResultStatus.Fail:
                this.setState({ message: result.message });
                break;
            default:
                throw new Error(`Invalid authentication result status '${result.status}'.`);
        }
    }

    render(): JSX.Element {
        const { action } = this.props;
        const { message } = this.state;

        if (message) {
            return <div>{message}</div>;
        }
        switch (action) {
            case LoginActions.Login:
                return <Loading label="Loggin in" useLogo />;
            case LoginActions.LoginCallback:
                return <Loading label="Processing login" useLogo />;
            default:
                throw new Error(`Invalid action '${action}'`);
        }
    }
}
